/**
 * MetricSparkline — 12-hour trailing micro-line for KPI tiles.
 *
 * Butler-600 stroke, no axes, no grid, no dots, no tooltip. Empty-data
 * rendering is explicit null (caller decides what to show in its place).
 *
 * Philosophy (per reference_recharts_butler_palette_mixpanel_restraint_20260420.md):
 * numbers are the loudest element; sparkline is decorative context.
 *
 * V2.5-B Fork (2026-04-20).
 */

import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts'

const BUTLER_600 = '#0070ab'

export interface SparklinePoint {
  /** Whatever key caller provides — but the component reads `value` only. */
  value: number
}

export interface MetricSparklineProps {
  /** Ordered trailing data. Last index = most recent. */
  data: SparklinePoint[]
  /** Height of the line box. Default 32px — tucks under the KPI number. */
  height?: number
  /** Override stroke color (default Butler-600). */
  stroke?: string
  /** Accessible label (the figure carries role="img" in the caller). */
  ariaLabel?: string
  'data-testid'?: string
}

export function MetricSparkline({
  data,
  height = 32,
  stroke = BUTLER_600,
  ariaLabel,
  'data-testid': testId = 'metric-sparkline',
}: MetricSparklineProps) {
  // Need ≥2 points to draw a line — one point is a dot, which reads as
  // noise, not a trend. Zero points = render nothing (caller owns the
  // "no trailing data" empty-state).
  if (!Array.isArray(data) || data.length < 2) return null

  const allZero = data.every((d) => !Number.isFinite(d.value) || d.value === 0)
  if (allZero) return null

  return (
    <div
      data-testid={testId}
      role="img"
      aria-label={ariaLabel ?? 'Sparkline showing recent trend'}
      className="mt-1"
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          {/* YAxis hidden but present so recharts scales to real data range. */}
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={1.75}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
